import React, { useMemo, useState } from "react";
import { Button, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ProgressBar from "../UI/ProgressBar";
import { useAlert } from "../../hooks/useAlert";
import {
  useGetMyPromotionInviteQuery,
  useRespondToPromotionInviteMutation,
} from "../../redux-toolkit/features/promotions/promotionsApiSlice";
import { PROMOTION_STATUSES } from "../../constants/promotionStatuses";
import {
  inviteDescriptionHasContent,
  sanitizeInviteDescriptionHtml,
} from "./sanitizeInviteDescription";
import {
  formatPromotionEventTime,
  formatUtcEventDate,
} from "../Super Admin/Promotions/promotionEventDateTime";
import { promotionLocationHref } from "../Super Admin/Promotions/promotionInviteFields";
import "./PromotionInviteResponse.css";

export const PromotionInviteResponse = () => {
  const { promotionId } = useParams();
  const navigate = useNavigate();
  const { displayAlert } = useAlert();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  const { data, isLoading, isError, error } = useGetMyPromotionInviteQuery(
    promotionId,
    { skip: !promotionId }
  );
  const [respond, { isLoading: isResponding }] =
    useRespondToPromotionInviteMutation();

  const payload = data?.data || {};
  const invite = payload.invite;
  const promotion = payload.promotion;
  const canRespond = Boolean(payload.canRespond);
  const locationHref = promotionLocationHref(invite?.locationUrl);

  const statusMessage = useMemo(() => {
    if (promotion?.status === PROMOTION_STATUSES.INVITE_ACCEPTED) {
      return "You have accepted this invitation.";
    }
    if (promotion?.status === PROMOTION_STATUSES.INVITE_REJECTED) {
      return "You have declined this invitation.";
    }
    if (!canRespond && invite) {
      return "This invitation is no longer open for a response.";
    }
    return "";
  }, [canRespond, invite, promotion?.status]);

  const submit = async (response, rejectionReason) => {
    try {
      const result = await respond({
        promotionId,
        response,
        reason: rejectionReason,
      }).unwrap();
      displayAlert({
        open: true,
        message:
          result?.message ||
          (response === "ACCEPT"
            ? "Invite accepted successfully"
            : "Invite rejected successfully"),
        type: "success",
        timeout: "2500",
      });
      setRejectOpen(false);
      setReason("");
      navigate("/quest", { replace: true });
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message || err?.message || "Unable to update this invitation",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const handleReject = () => {
    const trimmed = reason.trim();
    if (!trimmed) return;
    submit("REJECT", trimmed);
  };

  if (isLoading) {
    return (
      <div className="promo-invite-response">
        <ProgressBar />
      </div>
    );
  }

  if (isError || !invite) {
    return (
      <div className="promo-invite-response">
        <div className="promo-invite-response__card">
          <h1>Invitation unavailable</h1>
          <p className="promo-invite-response__unavailable">
            {error?.data?.message ||
              "This invitation could not be found, or it does not belong to your account."}
          </p>
          <Button
            variant="contained"
            disableElevation
            className="promo-invite-response__primary"
            onClick={() => navigate("/quest")}
          >
            Go to The Quest
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="promo-invite-response">
      <div className="promo-invite-response__card">
        <p className="promo-invite-response__eyebrow">Promotion invitation</p>
        <h1>{invite.name}</h1>

        <dl className="promo-invite-response__details">
          <div>
            <dt>Book</dt>
            <dd>{invite.book?.name || "—"}</dd>
          </div>
          <div>
            <dt>Date</dt>
            <dd>{formatUtcEventDate(invite.eventDate, { weekday: true })}</dd>
          </div>
          <div>
            <dt>Time</dt>
            <dd>
              {invite.hasEventTime === false
                ? "—"
                : formatPromotionEventTime(invite.eventDate, { hour12: true })}
            </dd>
          </div>
          <div>
            <dt>Venue</dt>
            <dd>{invite.venue || "—"}</dd>
          </div>
          {locationHref && (
            <div>
              <dt>Location</dt>
              <dd>
                <a
                  className="promo-invite-response__link"
                  href={locationHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {locationHref}
                </a>
              </dd>
            </div>
          )}
          {invite.createdBy?.name && (
            <div>
              <dt>Coordinator</dt>
              <dd>{invite.createdBy.name}</dd>
            </div>
          )}
        </dl>

        {inviteDescriptionHasContent(invite.description) && (
          <div
            className="promo-invite-response__description"
            dangerouslySetInnerHTML={{
              __html: sanitizeInviteDescriptionHtml(invite.description),
            }}
          />
        )}

        {statusMessage && (
          <p className="promo-invite-response__status">{statusMessage}</p>
        )}

        {promotion?.status === PROMOTION_STATUSES.INVITE_REJECTED &&
          promotion.rejectionReason && (
            <p className="promo-invite-response__reason">
              Reason: {promotion.rejectionReason}
            </p>
          )}

        {canRespond && !rejectOpen && (
          <div className="promo-invite-response__actions">
            <Button
              variant="contained"
              disableElevation
              className="promo-invite-response__muted"
              disabled={isResponding}
              onClick={() => setRejectOpen(true)}
            >
              Decline
            </Button>
            <Button
              variant="contained"
              disableElevation
              className="promo-invite-response__primary"
              disabled={isResponding}
              onClick={() => submit("ACCEPT")}
            >
              {isResponding ? "Saving…" : "Accept"}
            </Button>
          </div>
        )}

        {canRespond && rejectOpen && (
          <div className="promo-invite-response__reject">
            <p>Provide a reason for declining.</p>
            <TextField
              fullWidth
              multiline
              minRows={3}
              autoFocus
              placeholder="Enter rejection reason..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isResponding}
              inputProps={{ maxLength: 1000 }}
            />
            <p className="promo-invite-response__reject-note">
              This will be visible to admins.
            </p>
            <div className="promo-invite-response__actions">
              <Button
                variant="contained"
                disableElevation
                className="promo-invite-response__muted"
                disabled={isResponding}
                onClick={() => {
                  setRejectOpen(false);
                  setReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disableElevation
                className="promo-invite-response__danger"
                disabled={isResponding || !reason.trim()}
                onClick={handleReject}
              >
                {isResponding ? "Saving…" : "Decline invite"}
              </Button>
            </div>
          </div>
        )}

        {!canRespond && (
          <Button
            variant="contained"
            disableElevation
            className="promo-invite-response__primary"
            onClick={() => navigate("/quest")}
          >
            Go to The Quest
          </Button>
        )}
      </div>
    </div>
  );
};
